import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AppProvider, useApp } from "./context/AppContext";
import api from "./api";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import CashFlow from "./pages/CashFlow";
import Sales from "./pages/Sales";
import Purchases from "./pages/Purchases";
import Contacts from "./pages/Contacts";
import Contracts from "./pages/Contracts";
import Compliance from "./pages/Compliance";
import Vault from "./pages/Vault";
import Ledger from "./pages/Ledger";
import Reports from "./pages/Reports";
import Connectors from "./pages/Connectors";
import Services from "./pages/Services";
import Tasks from "./pages/Tasks";
import Boards from "./pages/Boards";
import Calendar from "./pages/Calendar";
import Users from "./pages/Users";
import Emails from "./pages/Emails";
import Forms from "./pages/Forms";

const AuthCallback = () => {
  const { setUser } = useApp();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = hash.split("session_id=")[1]?.split("&")[0];
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        setUser(res.data);
        navigate("/dashboard", { replace: true, state: { user: res.data } });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <div className="min-h-screen flex items-center justify-center text-muted-foreground">...</div>;
};

const Protected = ({ children }) => {
  const { user, authChecked } = useApp();
  const location = useLocation();
  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="auth-loading">...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "viewer" && !/^\/(boards|tasks|calendar)(\/|$)/.test(location.pathname)) return <Navigate to="/boards" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="cashflow" element={<CashFlow />} />
        <Route path="invoices" element={<Sales />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contracts" element={<Contracts />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="vault" element={<Vault />} />
        <Route path="ledger" element={<Ledger />} />
        <Route path="reports" element={<Reports />} />
        <Route path="connectors" element={<Connectors />} />
        <Route path="services" element={<Services />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="boards" element={<Boards />} />
        <Route path="boards/:boardId" element={<Tasks />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="users" element={<Users />} />
        <Route path="emails" element={<Emails />} />
        <Route path="forms" element={<Forms />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AppProvider>
    </div>
  );
}

export default App;
